import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Server, Trash2, Edit, Users, Eye, EyeOff, Loader2 } from 'lucide-react';

export default function SigmaServersPage() {
  const { api } = useAuthStore();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTokens, setShowTokens] = useState({});
  const [deleting, setDeleting] = useState(null);

  const [form, setForm] = useState({
    name: '',
    url: '',
    token: '',
    code: ''
  });

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sigma/servers');
      setServers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar servidores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (server = null) => {
    if (server) {
      setEditingServer(server);
      setForm({
        name: server.name,
        url: server.url,
        token: '',
        code: server.code
      });
    } else {
      setEditingServer(null);
      setForm({ name: '', url: '', token: '', code: '' });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.url) {
      toast.error('Preencha nome e URL do painel');
      return;
    }

    setSaving(true);
    try {
      if (editingServer) {
        const updateData = { name: form.name, url: form.url };
        if (form.token) updateData.token = form.token;
        await api.put(`/sigma/servers/${editingServer.id}`, updateData);
        toast.success('Servidor atualizado!');
      } else {
        await api.post('/sigma/servers', form);
        toast.success('Servidor criado!');
      }
      setModalOpen(false);
      fetchServers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serverId) => {
    if (!confirm('Tem certeza que deseja excluir este servidor?')) return;
    
    setDeleting(serverId);
    try {
      await api.delete(`/sigma/servers/${serverId}`);
      toast.success('Servidor excluído!');
      fetchServers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir servidor');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Server className="w-8 h-8 text-primary" />
            Servidores Sigma
          </h1>
          <p className="text-muted-foreground">Gerencie os servidores Sigma para integração</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Servidor
        </Button>
      </div>

      {servers.length === 0 ? (
        <Card className="p-12">
          <div className="text-center text-muted-foreground">
            <Server className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Nenhum servidor cadastrado</p>
            <p className="text-sm">Adicione um servidor Sigma para começar</p>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {servers.map((server) => (
            <Card key={server.id} className="relative">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{server.name}</CardTitle>
                    <p className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded mt-1 inline-block">
                      {server.code}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleOpenModal(server)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-500 hover:text-red-400"
                      onClick={() => handleDelete(server.id)}
                      disabled={deleting === server.id}
                    >
                      {deleting === server.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">URL</p>
                  <p className="text-sm font-mono truncate">{server.url}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Token</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-mono truncate flex-1">
                      {showTokens[server.id] ? server.token_preview : '••••••••••••'}
                    </p>
                    <button
                      onClick={() => setShowTokens(prev => ({ ...prev, [server.id]: !prev[server.id] }))}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {showTokens[server.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {server.linked_users_count} usuários vinculados
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criar/Editar */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingServer ? 'Editar Servidor' : 'Novo Servidor'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Servidor *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Servidor Premium"
              />
            </div>
            <div>
              <Label>URL do Painel *</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://painel.exemplo.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL base do painel Sigma (sem /webhook)
              </p>
            </div>
            <div>
              <Label>Token de API (opcional — legado)</Label>
              <Input
                type="password"
                value={form.token}
                onChange={(e) => setForm({ ...form, token: e.target.value })}
                placeholder="Deixe vazio para integração via login do painel"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Não é mais obrigatório. A integração agora é feita pelo login do painel (usuário e senha). O token só é necessário para o modo antigo (webhook).
              </p>
            </div>
            {!editingServer && (
              <div>
                <Label>Código (opcional)</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="Gerado automaticamente"
                  maxLength={8}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Código que os usuários usarão para vincular. Ex: SRV001
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
